'use client';

/**
 * GlassIconButton Component
 *
 * An icon button with glow effects on hover.
 * Automatically adapts to light/dark theme.
 * Includes built-in security props for permission/tier/license locking.
 *
 * @see Issue #383 - Permission-Aware UI Components with Lock Icon Pattern
 */

import LockIcon from '@mui/icons-material/Lock';
import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
import { alpha, styled, useTheme } from '@mui/material/styles';
import type { SecurityProps } from '@ops-matrix/shared-types';
import { motion } from 'framer-motion';
import React, { forwardRef, useCallback } from 'react';
import { useSecurityLock } from '../../hooks/useSecurityLock';
import { glassEffects, glowEffects } from '../../theme/shadows';
import { motionPresets, transitionPresets } from '../../theme/transitions';
import { GlassTooltip } from '../GlassTooltip';

// ============================================================================
// Types
// ============================================================================

/**
 * Icon button size variants for consistent UI density
 */
export enum GlassIconButtonSize {
  /** Extra small (28px) - for compact action groups */
  XS = 'xs',
  /** Small (32px) - for dense UIs */
  Small = 'small',
  /** Medium (36px) - default size */
  Medium = 'medium',
  /** Large (44px) - Apple touch target */
  Large = 'large',
}

export interface GlassIconButtonProps extends Omit<IconButtonProps, 'size'>, SecurityProps {
  /**
   * Button size
   * @default GlassIconButtonSize.Medium
   */
  size?: GlassIconButtonSize | 'xs' | 'small' | 'medium' | 'large';

  /**
   * Enable hover glow effect
   * @default false
   */
  glowOnHover?: boolean;

  /**
   * Glow color variant
   * @default 'primary'
   */
  glowColor?: 'primary' | 'secondary' | 'mixed';

  /**
   * Button variant
   * - 'subtle': Material flat icon button — transparent fill, state-layer on hover (default)
   * - 'standard': Same as subtle plus a row-hover icon-fade pattern (icon ghosted until parent row/card/list-item is hovered)
   * - 'outlined': Material outlined — transparent fill, 1px accent-tinted border
   * - 'glass': Apple liquid-glass (opt-in legacy) — use only when explicitly overlaid on photos / busy backgrounds, or as an "open/active" state indicator
   * @default 'subtle'
   */
  variant?: 'subtle' | 'standard' | 'outlined' | 'glass';

  /**
   * Enable entrance animation
   * @default false
   */
  animate?: boolean;

  /**
   * Animation delay in seconds
   * @default 0
   */
  animationDelay?: number;

  /**
   * Enable scale animation on hover
   * @default false
   */
  hoverScale?: boolean;

  /**
   * Tooltip text (shown when not locked)
   */
  tooltip?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledIconButtonProps {
  $isDark: boolean;
  $glowOnHover: boolean;
  $glowColor: 'primary' | 'secondary' | 'mixed';
  $hoverScale: boolean;
  $buttonVariant: 'subtle' | 'standard' | 'outlined' | 'glass';
  $buttonColor: IconButtonProps['color'];
  $size: GlassIconButtonSize;
}

// Size configurations — Apple HIG (44pt) / Material Design 3 (48dp recommended,
// 44dp acceptable). Padding grows the hit area around the icon without scaling
// the icon itself, so chrome density stays close while satisfying the 44pt
// minimum touch target. XS / Small remain below 44px and are reserved for
// purely-decorative or in-row affordances where the surrounding row is itself
// the primary hit target (e.g., row-action icons inside a 44px+ list item).
const sizeStyles: Record<GlassIconButtonSize, { padding: number; iconSize: number; totalSize: number }> = {
  [GlassIconButtonSize.XS]: { padding: 5, iconSize: 16, totalSize: 28 },
  [GlassIconButtonSize.Small]: { padding: 6, iconSize: 18, totalSize: 32 },
  [GlassIconButtonSize.Medium]: { padding: 12, iconSize: 20, totalSize: 44 }, // Apple HIG floor
  [GlassIconButtonSize.Large]: { padding: 12, iconSize: 24, totalSize: 48 }, // MD3 recommended
};

const getGlowValue = (color: 'primary' | 'secondary' | 'mixed', intensity: 'subtle' | 'medium'): string =>
  glowEffects[color][intensity];

const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledIconButtonProps>(
  ({ theme, $isDark, $glowOnHover, $glowColor, $hoverScale, $buttonVariant, $buttonColor, $size }) => {
    const glass = $isDark ? glassEffects.dark : glassEffects.light;
    const colorKey = $buttonColor === 'default' || $buttonColor === 'inherit' ? 'primary' : ($buttonColor ?? 'primary');
    const mainColor = theme.palette[colorKey]?.main ?? theme.palette.primary.main;
    const size = sizeStyles[$size];

    // Variants subtle/standard/outlined follow Material Design 3 icon-button
    // patterns (transparent fill, state-layer hover, MUI ripple on press).
    // The `glass` variant is the legacy Apple liquid-glass look — kept as an
    // explicit opt-in for callers that genuinely need it (e.g. floating
    // panels over photos, "open/active" indicators on a notification bell).
    return {
      position: 'relative',
      overflow: 'visible',
      transition: transitionPresets.button,
      borderRadius: '50%',
      width: size.totalSize,
      height: size.totalSize,
      padding: size.padding,
      '& .MuiSvgIcon-root': {
        fontSize: size.iconSize,
        position: 'relative' as const,
        zIndex: 2,
      },

      // ── Subtle (default) — Material Design 3 standard icon button ──
      ...($buttonVariant === 'subtle' && {
        backgroundColor: 'transparent',
        border: 'none',
        boxShadow: 'none',
      }),

      // ── Standard — Material flat with row-hover icon-fade pattern ──
      ...($buttonVariant === 'standard' && {
        backgroundColor: 'transparent',
        '& .MuiSvgIcon-root': {
          fontSize: size.iconSize,
          position: 'relative' as const,
          zIndex: 2,
          opacity: 0.45,
          transition: 'opacity 0.2s ease',
        },
        '&:hover .MuiSvgIcon-root': { opacity: 1 },
        '.MuiTableRow-root:hover &, .MuiCard-root:hover &, .MuiListItem-root:hover &, [data-hover]:hover &': {
          '& .MuiSvgIcon-root': { opacity: 1 },
        },
      }),

      // ── Outlined — Material outlined icon button (1px solid border) ──
      ...($buttonVariant === 'outlined' && {
        backgroundColor: 'transparent',
        border: `1px solid ${alpha(mainColor, 0.4)}`,
      }),

      // ── Glass — Apple liquid glass (legacy / opt-in) ──
      ...($buttonVariant === 'glass' && {
        backgroundColor: glass.background,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `0.5px solid ${glass.border}`,
        boxShadow: glass.shadow,
        '&::before': {
          content: '""',
          position: 'absolute' as const,
          top: 0,
          left: 0,
          right: 0,
          height: '50%',
          background: `linear-gradient(180deg, ${
            $isDark ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.50)'
          } 0%, transparent 100%)`,
          borderRadius: 'inherit',
          pointerEvents: 'none' as const,
          zIndex: 1,
        },
      }),

      // Hover state — variant-specific state-layer + opt-in glow/scale.
      // Consolidated into a single `&:hover` so later keys don't overwrite
      // earlier ones (object-spread caveat for duplicate keys).
      '&:hover': {
        ...($buttonVariant === 'subtle' && {
          backgroundColor: theme.palette.action.hover,
        }),
        ...($buttonVariant === 'standard' && {
          backgroundColor: theme.palette.action.hover,
        }),
        ...($buttonVariant === 'outlined' && {
          backgroundColor: alpha(mainColor, 0.08),
          borderColor: alpha(mainColor, 0.6),
        }),
        ...($buttonVariant === 'glass' && {
          backgroundColor: glass.backgroundHover,
          borderColor: glass.borderHover,
          boxShadow: glass.shadowHover,
        }),
        ...($glowOnHover && { boxShadow: getGlowValue($glowColor, 'subtle') }),
        ...($hoverScale && { transform: 'scale(1.1)' }),
      },

      '&.Mui-disabled': {
        opacity: 0.5,
        boxShadow: 'none',
      },
    };
  },
);

// ============================================================================
// Component
// ============================================================================

export const GlassIconButton = forwardRef<HTMLButtonElement, GlassIconButtonProps>(
  (
    {
      size = GlassIconButtonSize.Medium,
      glowOnHover = false,
      glowColor = 'primary',
      variant = 'subtle',
      animate = false,
      animationDelay = 0,
      hoverScale = false,
      color = 'primary',
      children,
      disabled,
      onClick,
      tooltip,
      // Security props
      permission,
      anyPermission,
      allPermissions,
      requiredTier,
      requiredLicense,
      anyLicense,
      showLockIcon = false, // Default false for icon buttons (replaces icon)
      onLockedClick,
      lockedTooltip,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    // Normalize string size to enum
    const normalizedSize = (typeof size === 'string' ? size : size) as GlassIconButtonSize;

    // Security lock state
    const { isLocked, handleLockedClick, lockedTooltipText } = useSecurityLock({
      permission,
      anyPermission,
      allPermissions,
      requiredTier,
      requiredLicense,
      anyLicense,
      onLockedClick,
      lockedTooltip,
    });

    const isDisabled = disabled || isLocked;

    // Handle click with security interception
    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (isLocked) {
          e.preventDefault();
          e.stopPropagation();
          handleLockedClick();
          return;
        }
        onClick?.(e);
      },
      [isLocked, handleLockedClick, onClick],
    );

    // Determine tooltip text
    const tooltipText = isLocked ? lockedTooltipText : tooltip;

    const button = (
      <StyledIconButton
        ref={ref}
        color={color}
        $isDark={isDark}
        $glowOnHover={glowOnHover}
        $glowColor={glowColor}
        $hoverScale={hoverScale}
        $buttonVariant={variant}
        $buttonColor={color}
        $size={normalizedSize}
        disabled={isDisabled}
        onClick={handleClick}
        {...props}
      >
        {isLocked && showLockIcon ? <LockIcon fontSize="small" /> : children}
      </StyledIconButton>
    );

    // Wrap with tooltip if we have tooltip text
    const wrappedButton = tooltipText ? (
      <GlassTooltip title={tooltipText} placement="top">
        {button}
      </GlassTooltip>
    ) : (
      button
    );

    if (!animate) {
      return wrappedButton;
    }

    return (
      <motion.span
        initial={motionPresets.scaleIn.initial}
        animate={motionPresets.scaleIn.animate}
        transition={{
          ...motionPresets.scaleIn.transition,
          delay: animationDelay,
        }}
        style={{ display: 'inline-flex' }}
      >
        {wrappedButton}
      </motion.span>
    );
  },
);

GlassIconButton.displayName = 'GlassIconButton';

// Export with proper type that includes children from IconButtonProps
export default GlassIconButton;
